import { NAV_SECTIONS } from "@/lib/nav-sections";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { UserRound, Bell, ChevronRight, Share2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Logo } from "./BrandMark";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { profileCoreProgress } from "@/lib/profile-progress";
import { tierByKey } from "@/lib/drives";
import { buildInviteLink, inviteShareText } from "@/lib/referral";
import { Button } from "@/components/ui/button";
import { useArenaNavVisible } from "@/components/ArenaHomeTile";
import { ARENA_COPY } from "@/lib/arena-copy";

export function SiteHeader() {
  const { user } = useAuth();
  const arenaVisible = useArenaNavVisible();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const expertsMode = useRouterState({ select: (s) => !!(s.location.search as { experts?: unknown }).experts });
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const updateHeight = () => {
      document.documentElement.style.setProperty("--site-header-height", `${header.offsetHeight}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const linkCls =
    "text-[15px] font-medium text-td-muted hover:text-td-dark transition data-[active=true]:text-td-dark data-[active=true]:font-semibold";

  return (
    <header ref={headerRef} className="sticky top-0 z-40 border-b border-td-line bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
      <div className="mx-auto flex max-w-[1120px] min-w-0 items-center gap-2 px-3 py-2.5 md:gap-5 md:px-6 md:py-3">
        {user ? (
          <>
            <div className="min-w-0 flex-1">
              <UserIdentityPill />
            </div>
            <nav className="hidden shrink-0 items-center gap-4 md:flex lg:gap-[22px]">
              <Link to="/home" className={linkCls} activeProps={{ "data-active": "true" } as any}>Главная</Link>
              {NAV_SECTIONS.filter((n) => n.to && !n.soon).map((n) => (
                <Link
                  key={n.key}
                  to={n.to!}
                  search={(n.search ?? {}) as never}
                  className={linkCls}
                  data-active={(n.to === "/people" ? pathname === "/people" && expertsMode === (n.key === "experts") : pathname.startsWith(n.to!)) ? "true" : undefined}
                >
                  {n.label}
                </Link>
              ))}
              {arenaVisible && (
                <Link to="/arena/people" className={linkCls} activeProps={{ "data-active": "true" } as any}>{ARENA_COPY.navLabel}</Link>
              )}
            </nav>
            <HeaderActions />
          </>
        ) : (
          <>
            <div className="min-w-0 flex-1"><Logo /></div>
            <nav className="flex items-center gap-3 md:gap-[26px]">
              <Link
                to="/auth"
                className="rounded-full border border-td-teal bg-transparent px-5 py-2.5 text-[14px] font-semibold text-td-teal transition hover:bg-primary-50 md:px-[22px] md:py-[11px] md:text-[15px]"
              >
                Войти
              </Link>
            </nav>
          </>
        )}
      </div>
    </header>
  );
}

function UserIdentityPill() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { data: statusKey } = useQuery({
    queryKey: ["resident-status", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase.rpc("resident_status_key", { uid: user.id });
      if (error) throw error;
      return data;
    },
  });

  const name = data?.full_name ?? "";
  const progress = profileCoreProgress(data);
  const status = tierByKey(statusKey)?.label ?? "Мой профиль";
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Link
      to="/profile"
      className="flex w-full min-w-0 max-w-[230px] items-center gap-2 rounded-2xl border border-td-line bg-white px-1.5 py-1 pr-2 transition hover:border-td-teal"
      aria-label="Мой профиль"
    >
      <span className="relative h-9 w-9 shrink-0">
        {data?.photo_url ? (
          <img
            src={data.photo_url}
            alt=""
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-td-soft text-[12px] font-semibold text-td-teal">
            {initials || <UserRound className="h-4 w-4" aria-hidden="true" />}
          </span>
        )}
        {progress.done < progress.total && (
          <svg className="pointer-events-none absolute -inset-0.5 h-10 w-10 -rotate-90 text-td-teal" viewBox="0 0 40 40" aria-hidden="true">
            <circle cx="20" cy="20" r="18.5" fill="none" stroke="currentColor" strokeOpacity="0.16" strokeWidth="1.5" />
            <circle cx="20" cy="20" r="18.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" pathLength="100" strokeDasharray={`${progress.done * 20} 100`} />
          </svg>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-[13px] font-semibold text-td-dark">
          {name || "Профиль"}
        </span>
        <span className="truncate text-[11px] font-normal text-td-muted">{status}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-td-muted" aria-hidden="true" />
    </Link>
  );
}

function HeaderActions() {
  const { user } = useAuth();
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const shareInvite = async () => {
    if (!profile?.username) {
      toast.error("Сначала заполни профиль");
      return;
    }
    const link = buildInviteLink(profile.username);
    const text = inviteShareText(link);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "TurkeyDrive", text });
      } catch {
        // Системное меню можно закрыть без каких-либо действий.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Ссылка-приглашение скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Button type="button" variant="outline" size="icon" onClick={shareInvite} className="h-10 w-10 rounded-full border-td-line bg-white shadow-none" aria-label="Поделиться приглашением">
        <Share2 className="h-[18px] w-[18px] text-td-muted" aria-hidden="true" />
      </Button>
      <NotificationBell />
    </div>
  );
}

function NotificationBell() {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onNotifications = pathname === "/notifications";

  const { data: unread } = useQuery({
    queryKey: ["notifications", "unread"],
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("notifications_unread_count");
      if (error) throw error;
      return data ?? 0;
    },
  });

  // На экране уведомлений колокольчик закрывает экран: назад по истории, иначе на главную
  const goBack = () => {
    if (router.history.canGoBack()) router.history.back();
    else router.navigate({ to: "/home" });
  };

  if (onNotifications) {
    return (
      <button
        type="button"
        onClick={goBack}
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-td-teal bg-primary-50 transition hover:border-td-dark"
        aria-label="Закрыть уведомления и вернуться назад"
      >
        <Bell className="h-4.5 w-4.5 text-td-teal" aria-hidden="true" />
      </button>
    );
  }

  return (
    <Link
      to="/notifications"
      className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-td-line bg-white transition hover:border-td-teal"
      aria-label={unread ? `Уведомления, непрочитанных: ${unread}` : "Уведомления"}
    >
      <Bell className="h-4.5 w-4.5 text-td-muted" aria-hidden="true" />
      {!!unread && unread > 0 && (
        <span className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-4 text-white">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Link>
  );
}
